import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Palette, Search, Warehouse, Star, Video, ArrowRight } from 'lucide-react';
import { services } from '../mockData';

const iconMap = {
  Truck: Truck,
  Palette: Palette,
  Search: Search,
  Warehouse: Warehouse,
  Star: Star,
  Video: Video
};

const Services = ({ showAll = false }) => {
  const displayServices = showAll ? services : services.slice(0, 6);

  return (
    <section className="py-24 bg-gradient-to-b from-white via-purple-50/30 to-white relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-purple-200 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute bottom-40 left-10 w-96 h-96 bg-pink-200 rounded-full opacity-20 blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Creative Section Header */}
        <div className="text-center mb-20">
          <p className="text-purple-600 font-bold text-sm uppercase tracking-widest mb-4 flex items-center justify-center space-x-2">
            <span className="w-8 h-0.5 bg-purple-600"></span>
            <span>What We Offer</span>
            <span className="w-8 h-0.5 bg-purple-600"></span>
          </p>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 mb-6">
            <span className="block">End-to-End</span>
            <span className="gradient-text">Fulfillment Solutions</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Everything you need to scale your ecommerce business
          </p>
        </div>

        {/* Creative Staggered Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayServices.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            // Staggered positioning
            const isStaggered = index % 3 === 1;
            
            return (
              <div
                key={service.id}
                className={`group relative ${isStaggered ? 'lg:mt-12' : ''}`}
              >
                {/* Creative Card with Hover Effects */}
                <div className="relative bg-white rounded-3xl p-8 shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-purple-200 transform hover:-translate-y-2 h-full">
                  {/* Decorative Corner */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-100 to-transparent rounded-tr-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Icon with Creative Background */}
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-200 to-pink-200 rounded-2xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
                    <div className="relative w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center transform group-hover:rotate-6 group-hover:scale-110 transition-all duration-300">
                      <IconComponent size={36} className="text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-purple-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Creative Link */}
                  <Link
                    to="/services"
                    className="inline-flex items-center text-purple-600 font-bold hover:text-purple-700 group-hover:gap-3 transition-all duration-300"
                  >
                    <span>Learn More</span>
                    <ArrowRight size={18} className="ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </Link>

                  {/* Hover Glow Effect */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/5 group-hover:to-pink-500/5 transition-all duration-300 pointer-events-none"></div>
                </div>

                {/* Floating Number */}
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                  {index + 1}
                </div>
              </div>
            );
          })}
        </div>

        {/* Creative CTA at Bottom */}
        {!showAll && (
          <div className="text-center mt-16">
            <Link
              to="/services"
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <span>View All Services</span>
              <ArrowRight size={24} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;
